import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiChevronLeft, FiChevronRight, FiClock, FiUsers, FiCheckCircle, FiAlertCircle, FiX, FiCalendar, FiPlus } = FiIcons;

const CalendarView = ({ exams, currentDate, onDateChange, viewMode, onExamClick, onMarkComplete, onMarkAbsentees, onScheduleExam }) => {
  const [selectedDate, setSelectedDate] = useState(null);

  const getCalendarDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    const days = [];
    const current = new Date(startDate);

    for (let i = 0; i < 42; i++) {
      days.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }

    return days;
  };

  const getExamsForDate = (date) => {
    const dateStr = date.toDateString();
    return exams.filter(exam => new Date(exam.date).toDateString() === dateStr);
  };

  const getUpcomingExams = () => {
    const today = new Date();
    return exams
      .filter(exam => new Date(exam.date) >= today && exam.status !== 'completed' && exam.status !== 'cancelled')
      .sort((a, b) => new Date(a.date) - new Date(b.date))
      .slice(0, 5);
  };

  const getCompletedExams = () => {
    return exams
      .filter(exam => exam.status === 'completed')
      .sort((a, b) => new Date(b.date) - new Date(a.date))
      .slice(0, 5);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'scheduled': return 'bg-blue-500';
      case 'in-progress': return 'bg-yellow-500';
      case 'completed': return 'bg-green-500';
      case 'cancelled': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + direction);
    onDateChange(newDate);
  };

  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isCurrentMonth = (date) => {
    return date.getMonth() === currentDate.getMonth();
  };

  const handleDateClick = (date) => {
    // Always set the selected date when a date is clicked
    if (selectedDate === date.toDateString()) {
      setSelectedDate(null);
    } else {
      setSelectedDate(date.toDateString());
    }
    
    // If user wants to schedule an exam on this date and it's a future date
    if (onScheduleExam && isCurrentMonth(date) && new Date(date) >= new Date()) {
      onScheduleExam(date);
    }
  };

  // For formatting date in "YYYY-MM-DD" format for the date input
  const formatDateForInput = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  if (viewMode === 'month') {
    const days = getCalendarDays();
    const upcomingExams = getUpcomingExams();
    const completedExams = getCompletedExams();

    return (
      <>
        {/* Info Bar */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Upcoming Exams */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-100">
            <div className="p-4 border-b border-gray-200 flex justify-between items-center">
              <div className="flex items-center space-x-2">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <SafeIcon icon={FiCalendar} className="text-blue-600" />
                </div>
                <h3 className="text-lg font-medium text-gray-800">Upcoming Exams & Quizzes</h3>
              </div>
              {upcomingExams.length > 0 && (
                <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
                  {upcomingExams.length} upcoming
                </span>
              )}
            </div>

            <div className="divide-y divide-gray-100">
              {upcomingExams.length > 0 ? (
                upcomingExams.map((exam) => {
                  const examDate = new Date(exam.date);
                  const isExamToday = isToday(examDate);
                  const daysTillExam = Math.ceil((examDate - new Date()) / (1000 * 60 * 60 * 24));
                  
                  return (
                    <div 
                      key={exam.id} 
                      className="p-4 hover:bg-gray-50 transition-colors cursor-pointer"
                      onClick={() => onExamClick(exam)}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium text-gray-800">{exam.title}</div>
                          <div className="text-sm text-gray-600">{exam.courseCode} • {exam.classCode}</div>
                          <div className="flex items-center mt-1 text-xs text-gray-500">
                            <SafeIcon icon={FiClock} className="mr-1" />
                            {examDate.toLocaleDateString()} at {exam.time}
                            {isExamToday && (
                              <span className="ml-2 px-2 py-0.5 bg-red-100 text-red-800 rounded-full">Today</span>
                            )}
                            {!isExamToday && daysTillExam === 1 && (
                              <span className="ml-2 px-2 py-0.5 bg-orange-100 text-orange-800 rounded-full">Tomorrow</span>
                            )}
                          </div>
                        </div>
                        <div className={`px-3 py-1 text-xs font-medium rounded-full ${
                          exam.status === 'in-progress' ? 'bg-yellow-100 text-yellow-800' : 'bg-blue-100 text-blue-800'
                        }`}>
                          {exam.status === 'in-progress' ? 'In Progress' : 'Scheduled'}
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="py-8 text-center text-gray-500">
                  <SafeIcon icon={FiCalendar} className="mx-auto text-3xl mb-2 opacity-40" />
                  <p>No upcoming exams</p>
                  <button 
                    onClick={() => onScheduleExam && onScheduleExam(new Date())}
                    className="mt-2 px-3 py-1 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 inline-flex items-center"
                  >
                    <SafeIcon icon={FiPlus} className="mr-1" />
                    Schedule Exam
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Completed Exams */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-100">
            <div className="p-4 border-b border-gray-200 flex justify-between items-center">
              <div className="flex items-center space-x-2">
                <div className="p-2 bg-green-100 rounded-lg">
                  <SafeIcon icon={FiCheckCircle} className="text-green-600" />
                </div>
                <h3 className="text-lg font-medium text-gray-800">Completed Exams & Quizzes</h3>
              </div>
              {completedExams.length > 0 && (
                <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">
                  {completedExams.length} completed
                </span>
              )}
            </div>

            <div className="divide-y divide-gray-100">
              {completedExams.length > 0 ? (
                completedExams.map((exam) => (
                  <div 
                    key={exam.id} 
                    className="p-4 hover:bg-gray-50 transition-colors cursor-pointer"
                    onClick={() => onExamClick(exam)}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium text-gray-800">{exam.title}</div>
                        <div className="text-sm text-gray-600">{exam.courseCode} • {exam.classCode}</div>
                        <div className="flex items-center mt-1 text-xs text-gray-500">
                          <SafeIcon icon={FiClock} className="mr-1" />
                          {new Date(exam.date).toLocaleDateString()} • {exam.actualTakers || 0}/{exam.expectedTakers} takers
                        </div>
                      </div>
                      <div className="px-3 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                        Completed
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="py-8 text-center text-gray-500">
                  <SafeIcon icon={FiCheckCircle} className="mx-auto text-3xl mb-2 opacity-40" />
                  <p>No completed exams</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Calendar */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-100">
          {/* Calendar Header */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-semibold text-gray-800">
                {currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </h3>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => navigateMonth(-1)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <SafeIcon icon={FiChevronLeft} />
                </button>
                <button
                  onClick={() => onDateChange(new Date())}
                  className="px-3 py-1 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                >
                  Today
                </button>
                <button
                  onClick={() => navigateMonth(1)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <SafeIcon icon={FiChevronRight} />
                </button>
              </div>
            </div>
          </div>

          {/* Calendar Grid */}
          <div className="p-6">
            {/* Day Headers */}
            <div className="grid grid-cols-7 gap-2 mb-4">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className="p-3 text-center text-sm font-medium text-gray-500">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Days */}
            <div className="grid grid-cols-7 gap-2">
              {days.map((day, index) => {
                const dayExams = getExamsForDate(day);
                const isCurrentMonthDay = isCurrentMonth(day);
                const isTodayDate = isToday(day);
                const isPastDate = day < new Date() && !isTodayDate;
                const isSelected = selectedDate === day.toDateString();

                return (
                  <motion.div
                    key={index}
                    className={`min-h-[120px] p-2 border rounded-lg cursor-pointer transition-all ${
                      isSelected
                        ? 'border-primary-500 bg-primary-50 shadow-md'
                        : isTodayDate
                        ? 'border-primary-500 bg-primary-50'
                        : isCurrentMonthDay
                        ? isPastDate 
                          ? 'border-gray-200 hover:border-gray-300 hover:bg-gray-50 opacity-70'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        : 'border-gray-100 bg-gray-50 opacity-50'
                    }`}
                    onClick={() => handleDateClick(day)}
                    whileHover={{ scale: 1.02 }}
                  >
                    <div className="flex justify-between items-center mb-1">
                      <div className={`text-sm font-medium ${
                        isTodayDate
                          ? 'text-primary-700'
                          : isCurrentMonthDay
                          ? isPastDate ? 'text-gray-500' : 'text-gray-900'
                          : 'text-gray-400'
                      }`}>
                        {day.getDate()}
                      </div>
                      
                      {/* Add button for future dates */}
                      {isCurrentMonthDay && !isPastDate && onScheduleExam && (
                        <motion.button
                          className="p-1 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-full"
                          onClick={(e) => {
                            e.stopPropagation();
                            onScheduleExam(day);
                          }}
                          whileHover={{ scale: 1.1 }}
                          title="Schedule exam"
                        >
                          <SafeIcon icon={FiPlus} className="text-xs" />
                        </motion.button>
                      )}
                    </div>

                    <div className="space-y-1">
                      {dayExams.slice(0, 3).map(exam => (
                        <motion.div
                          key={exam.id}
                          className={`p-1 rounded text-xs text-white cursor-pointer ${getStatusColor(exam.status)}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            onExamClick(exam);
                          }}
                          whileHover={{ scale: 1.05 }}
                          title={`${exam.title} - ${exam.time}`}
                        >
                          <div className="truncate font-medium">{exam.courseCode}</div>
                          <div className="truncate opacity-90">{exam.time}</div>
                        </motion.div>
                      ))}
                      {dayExams.length > 3 && (
                        <div className="text-xs text-gray-500 font-medium">
                          +{dayExams.length - 3} more
                        </div>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>

          {/* Selected Date Details */}
          {selectedDate && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="border-t border-gray-200 p-6"
            >
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-lg font-semibold text-gray-800">
                  {new Date(selectedDate).toLocaleDateString('en-US', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </h4>
                <div className="flex items-center space-x-2">
                  {onScheduleExam && new Date(selectedDate) >= new Date() && (
                    <button
                      onClick={() => onScheduleExam(new Date(selectedDate))}
                      className="px-3 py-1 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center space-x-1"
                    >
                      <SafeIcon icon={FiPlus} className="text-xs" />
                      <span>Schedule Exam</span>
                    </button>
                  )}
                  <button
                    onClick={() => setSelectedDate(null)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <SafeIcon icon={FiX} />
                  </button>
                </div>
              </div>

              <div className="space-y-3">
                {getExamsForDate(new Date(selectedDate)).map(exam => (
                  <div
                    key={exam.id}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                    onClick={() => onExamClick(exam)}
                  >
                    <div className="flex items-center space-x-4">
                      <div className={`w-3 h-3 rounded-full ${getStatusColor(exam.status)}`}></div>
                      <div>
                        <div className="font-medium text-gray-900">{exam.title}</div>
                        <div className="text-sm text-gray-600">
                          {exam.courseCode} - {exam.classCode} • {exam.time}
                        </div>
                        <div className="text-xs text-gray-500 flex items-center space-x-3">
                          <span className="flex items-center">
                            <SafeIcon icon={FiUsers} className="mr-1" />
                            {exam.actualTakers || exam.expectedTakers} takers
                          </span>
                          <span className="flex items-center">
                            <SafeIcon icon={FiClock} className="mr-1" />
                            {exam.duration} min
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      {exam.status === 'scheduled' && onMarkComplete && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onMarkComplete(exam.id);
                          }}
                          className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                          title="Mark as completed"
                        >
                          <SafeIcon icon={FiCheckCircle} />
                        </button>
                      )}
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        exam.status === 'completed' ? 'bg-green-100 text-green-800' :
                        exam.status === 'in-progress' ? 'bg-yellow-100 text-yellow-800' :
                        exam.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                        'bg-blue-100 text-blue-800'
                      }`}>
                        {exam.status}
                      </span>
                    </div>
                  </div>
                ))}

                {getExamsForDate(new Date(selectedDate)).length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <SafeIcon icon={FiCalendar} className="mx-auto text-3xl mb-2" />
                    <p>No exams scheduled for this date</p>
                    {onScheduleExam && new Date(selectedDate) >= new Date() && (
                      <button
                        onClick={() => onScheduleExam(new Date(selectedDate))}
                        className="mt-2 px-3 py-1 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center space-x-1 mx-auto"
                      >
                        <SafeIcon icon={FiPlus} />
                        <span>Schedule Exam</span>
                      </button>
                    )}
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </div>
      </>
    );
  }

  // Week and Day views can be implemented similarly
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
      <div className="text-center py-12 text-gray-500">
        <SafeIcon icon={FiClock} className="mx-auto text-4xl mb-4" />
        <p>Week and Day views coming soon!</p>
      </div>
    </div>
  );
};

export default CalendarView;